<template>
  <div
    ref="container"
    :class="
      $b({
        partsMode: ['parts', 'conversionkit'].includes(
          customisedProduct.product.pageMode
        ),
      })
    "
    :style="`--row-height: ${rowHeight}px`"
  >
    <div :class="$b('inner')">
      <CustomiserTabs
        v-if="parts.length > 1 && !isLRK"
        rounded
        :tabs="parts"
        :active-tab-index="activeTabIndex"
        :icons-follow-lens="iconsFollowLens"
        :logos-follow-lens="logosFollowLens"
        :bottom-follow-frame="bottomFollowFrame"
        :frame-follow-strap="frameFollowStrap"
        :container-width="containerWidth"
        :is-ck-or-parts="isCkOrParts"
        :show-on-desktop="forceTabs"
        :class="$b('part-tabs')"
        @close-customiser="$emit('close-customiser')"
        @update:active-tab-index="$emit('update:activeTabIndex', $event)"
        @unlink-icons-and-lenses="$emit('unlink-icons-and-lenses')"
        @toggle-frame-strap-link="$emit('toggle-frame-strap-link')"
      />
      <div
        ref="rows-container"
        :class="$b('rows-container')"
      >
        <div
          v-for="(part, idx) in parts"
          :ref="`customiser-tab-${idx}`"
          :key="part.skuPrefix"
          :class="
            $b('rows', {
              'tab-on-desktop': isCkOrParts,
              first: idx === 0,
              showTabs: isCkOrParts,
              isPrev: idx < activeTabIndex,
              isActive: idx === activeTabIndex,
              isNext: idx > activeTabIndex,
            })
          "
        >
          <CustomiserRow
            ref="rows"
            :class="$b('row')"
            :part="part"
            :customised-product="customisedProduct"
            :icons-follow-lens="iconsFollowLens"
            :logos-follow-lens="logosFollowLens"
            :bottom-follow-frame="bottomFollowFrame"
            :frame-follow-strap="frameFollowStrap"
            :tabs-on-desktop="isCkOrParts"
            :single-line-swatches="singleLineSwatches"
            :show-heading="showChoiceHeading"
            :first-row="idx === 0"
            :prescription-mode="prescriptionMode"
            @select="$emit('select', $event)"
            @toggle-bottom-frame-link="$emit('toggle-bottom-frame-link')"
            @unlink-icons-and-lenses="$emit('unlink-icons-and-lenses')"
            @hide-icon-lens-link="showIconLensLink = false"
            @show-icon-lens-link="showIconLensLink = true"
            @hide-frame-strap-link="showFrameFollowStrap = false"
            @show-frame-strap-link="showFrameFollowStrap = true"
            @prescription-mode-toggled="$emit('prescription-mode-toggled', $event)"
          >
            <template
              v-if="part.groupParts[0]?.isLensPart"
              #afterChoiceName
            >
              <template v-if="(customisedProduct.lensChoice || {}).isIrisLens">
                <BaseToggle
                  :class="$b('iris-toggle')"
                  :model-value="showIrisPreview"
                  text-transform="capitalize"
                  @input="(state) => $emit('iris-preview-state-changed', state)"
                >
                  <template #off-icon>
                    <VisionIcon
                      colour="black"
                      size="x-small"
                      icon="cloudy"
                    />
                  </template>

                  <template #on-icon>
                    <VisionIcon
                      colour="black"
                      size="x-small"
                      icon="day-sunny"
                    />
                  </template>
                </BaseToggle>
              </template>
            </template>

            <VisionLink
              v-if="part.groupParts[0]?.isLensPart"
              is-button
              line-link
              theme="gradient"
              :mono-font="true"
              :class="$b('info-button')"
              @click="$emit('show-lens-guide')"
            >
              {{ $tr('Lens Guide') }}
            </VisionLink>
            <PartLinkButton
              v-if="part.groupName === 'icons' && showIconLensLink"
              :class="$b('link-button')"
              :is-linked="iconsFollowLens"
              :link-to-lenses="true"
              @click="toggleIconLensLink"
            >
              {{ $tr('Match to Lenses') }}
            </PartLinkButton>
            <PartLinkButton
              v-if="part.groupName === 'logos' && showIconLensLink"
              :class="$b('link-button')"
              :is-linked="logosFollowLens"
              :link-to-lenses="true"
              @click="toggleIconLensLink"
            >
              {{ $tr('Match to Lenses') }}
            </PartLinkButton>
            <PartLinkButton
              v-if="
                part.groupName === 'frame' && part?.isSnow && showFrameFollowStrap
              "
              :class="$b('link-button')"
              :is-linked="frameFollowStrap"
              :link-to-strap="true"
              @click="toggleFrameFollowStrap"
            >
              {{ $tr('Match to Strap') }}
            </PartLinkButton>
          </CustomiserRow>
        </div>
      </div>
      <div
        v-if="pageMode === 'custom'"
        :class="$b('button-container')"
      >
        <CustomiserButtonB
          v-if="!forceTabs"
          :enabled="true"
          :class="$b('customiser-save')"
          @click="$emit('close-customiser')"
        >
          {{ $tr('Save Your Design') }}
        </CustomiserButtonB>
      </div>
    </div>
  </div>
</template>

<script>
import CustomisedProduct from '~/models/CustomisedProduct'

export default {
  name: 'ProductCustomiser',
  props: {
    customisedProduct: { type: CustomisedProduct, required: true },
    activeTabIndex: { type: Number, required: true },
    showIrisPreview: { type: Boolean, default: false },
    limitedEditionCount: { type: Number, default: 0 },
    containerWidth: { type: Number, required: true },
    iconsFollowLens: { type: Boolean, default: false },
    logosFollowLens: { type: Boolean, default: false },
    bottomFollowFrame: { type: Boolean, default: false },
    frameFollowStrap: { type: Boolean, default: false },
    singleLineSwatches: { type: Boolean, default: false },
    showChoiceHeading: { type: Boolean, default: true },
    forceTabs: { type: Boolean, default: false },
    prescriptionMode: { type: Boolean, default: false },
  },
  emits: [
    'close-customiser',
    'update:activeTabIndex',
    'unlink-icons-and-lenses',
    'show-lens-guide',
    'iris-preview-state-changed',
    'toggle-icon-lens-link',
    'toggle-frame-strap-link',
    'toggle-bottom-frame-link',
    'select',
    'prescription-mode-toggled'
  ],
  setup() {
    const bem = useBEM('ProductCustomiser')

    return {
      bem,
    }
  },
  data() {
    return {
      rowHeight: 160,
      showFrameFollowStrap: true,
      showIconLensLink: true,
    }
  },
  computed: {
    parts() {
      let partsWithoutFrame

      if (this.isCkOrParts) {
        partsWithoutFrame = this.customisedProduct.visibleParts.map((part) => {
          return {
            groupName: part.name.toLowerCase(),
            groupParts: [part],
          }
        })
      } else {
        partsWithoutFrame = this.customisedProduct.visibleParts
          .filter((part) => part.name !== 'Frame' && part.name !== 'Bottom')
          .map((filteredPart) => {
            return {
              groupName: filteredPart.name.toLowerCase(),
              groupParts: [filteredPart],
            }
          })
      }

      const frameParts = this.customisedProduct.visibleParts.filter(
        (part) =>
          (part.name === 'Frame' || part.name === 'Bottom') && !this.isSnow
      )
      let frameGroup
      if (frameParts.length && !this.isCkOrParts) {
        frameGroup = {
          groupName: 'frame',
          groupParts: frameParts,
        }
        return [frameGroup, ...partsWithoutFrame]
      }

      const snowParts = this.customisedProduct.visibleParts.map(
        (filteredPart) => {
          return {
            groupName: filteredPart.name.toLowerCase(),
            isSnow: this.isSnow,
            groupParts: [filteredPart],
          }
        }
      )

      return this.isSnow ? [...snowParts] : [...partsWithoutFrame]
    },
    isSinglePart() {
      return this.parts.length === 1
    },
    pageMode() {
      return this.customisedProduct.product?.pageMode
    },
    isCkOrParts() {
      return ['parts', 'conversionkit'].includes(
        this.customisedProduct.product.pageMode
      )
    },
    isLRK() {
      return this.pageMode === 'lenses'
    },
    isSnow() {
      if (!this.customisedProduct?.lensChoice?.lensTech) return false
      return this.customisedProduct.lensChoice.lensTech.includes('snow')
    },
  },
  watch: {
    activeTabIndex(tabIndex) {
      this.setActiveTabHeight(tabIndex)
      // Update active part if in parts mode
      if (!['parts', 'conversionkit'].includes(this.pageMode)) return
      const part = this.parts[tabIndex]?.groupParts[0]
      this.customisedProduct.setActivePart(part.stockManagedSKUPrefix)
    },
  },
  methods: {
    toggleIconLensLink() {
      this.$emit('toggle-icon-lens-link')
    },
    toggleFrameFollowStrap() {
      this.$emit('toggle-frame-strap-link')
    },
    setActiveTabHeight(i) {
      const containerElem = this.$refs['rows-container']
      const currentHeight = containerElem.clientHeight

      containerElem.style.height = `${currentHeight}px`
      
      const refName = `customiser-tab-${i}`
      const newHeight = this.$refs[refName][0].clientHeight

      requestAnimationFrame(() => {
        containerElem.style.height = `${newHeight}px`
      })
      
      setTimeout(() => {
        containerElem.style.height = 'auto'
      }, 300);
    },
  },
}
</script>

<style lang="postcss">
.ProductCustomiser {
  @apply z-[4]
}

.ProductCustomiser--parts-mode .ProductCustomiser__part-tabs {
  @screen md {
    @apply mt-md;
  }
}

.ProductCustomiser__rows-container {
  @screen mdMax {
    @apply relative h-fit flex flex-col overflow-hidden
    transition-[height] duration-300;
  }
}

.ProductCustomiser__rows {
  @apply relative block;

  &--first {
    .ProductCustomiser__row {
      @apply md:pt-sm;
    }
  }

  .ProductCustomiser--parts-mode & {
    @apply mt-0;
  }

  @screen mdMax {
    @apply opacity-0 pointer-events-none absolute
    duration-300 w-full delay-0;
    transition-property: opacity, transform;

    &--is-active {
      @apply opacity-100 block pointer-events-auto relative delay-[150ms];
    }

    &--is-prev {
      @apply -translate-x-[calc(var(--outer-gutter)_-_8px)];
    }

    &--is-next {
      @apply translate-x-[calc(var(--outer-gutter)_-_8px)];
    }
  }
}

.ProductCustomiser__rows--tab-on-desktop {
  @apply opacity-0 pointer-events-none absolute
  duration-300 w-full delay-0;
  transition-property: opacity, transform;

  &.ProductCustomiser__rows--is-active {
    @apply opacity-100 block pointer-events-auto relative delay-[150ms];
  }

  &.ProductCustomiser__rows--is-prev {
    @apply -translate-x-[calc(var(--outer-gutter)_-_8px)];
  }

  &.ProductCustomiser__rows--is-next {
    @apply translate-x-[calc(var(--outer-gutter)_-_8px)];
  }
}

.ProductCustomiser__customiser-save {
  @apply md:mb-10;
}

.ProductCustomiser__button-container {
  @apply pt-xs;
}

.ProductCustomiser__iris-toggle {
  @apply justify-start;

  p {
    @apply font-semibold;
  }

  .BaseToggle__off-icon,
  .BaseToggle__on-icon {
    margin-top: -3px;
  }
}

.ProductCustomiser__info-button {
  @apply text-tiny whitespace-nowrap;
}
</style>
